import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2, XCircle } from "lucide-react";
import { API, BACKEND_URL } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await API.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", token: data.download_token, title: data.photo_title });
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setState({ phase: "failed" });
          return;
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setState({ phase: "error" });
          return;
        }
      }
      attempts.current += 1;
      if (attempts.current < 30) timer = setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-lg w-full text-center"
      >
        {state.phase === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="animate-spin text-[#D4AF37] mx-auto mb-8" size={40} />
            <h1 className="font-display text-4xl text-white mb-4">Confirming your payment</h1>
            <p className="text-sm text-zinc-400">Hold on a moment while we prepare your download.</p>
          </div>
        )}
        {state.phase === "paid" && (
          <div data-testid="payment-confirmed">
            <span className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] block mb-6">Payment confirmed</span>
            <h1 className="font-display text-5xl md:text-6xl text-white tracking-tighter leading-none mb-6">
              <em>Yours,</em> forever.
            </h1>
            <p className="text-sm text-zinc-400 mb-10">
              {state.title ? `"${state.title}" is ready.` : "Your photograph is ready."} Download the full-resolution
              file below — keep this link safe. We've also emailed it to you.
            </p>
            <a
              href={`${BACKEND_URL}/api/download/${state.token}`}
              data-testid="download-photo-button"
              className="inline-flex items-center gap-3 bg-white text-black text-xs tracking-[0.25em] uppercase font-semibold px-12 py-6 hover:bg-[#D4AF37] hover:-translate-y-0.5 transition-all duration-300"
            >
              <Download size={16} /> Download your photograph
            </a>
            <div className="mt-8">
              <Link to="/gallery" className="text-xs tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors duration-300" data-testid="success-back-link">
                Continue browsing
              </Link>
            </div>
          </div>
        )}
        {(state.phase === "failed" || state.phase === "error" || state.phase === "timeout") && (
          <div data-testid="payment-problem">
            <XCircle className="text-zinc-600 mx-auto mb-8" size={40} />
            <h1 className="font-display text-4xl text-white mb-4">We couldn't confirm this payment</h1>
            <p className="text-sm text-zinc-400 mb-10">
              {state.phase === "timeout"
                ? "Confirmation is taking longer than usual. If you were charged, your download link will arrive shortly — check back from your receipt."
                : "The session was not completed. You have not been charged."}
            </p>
            <Link
              to="/gallery"
              data-testid="problem-back-link"
              className="inline-flex items-center gap-3 border border-white/25 text-white text-xs tracking-[0.25em] uppercase font-semibold px-12 py-5 hover:border-white transition-colors duration-300"
            >
              Back to the gallery
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
